import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Grid } from '@mui/material';
import KpiCard from '../common/KpiCard';
import KpiWithRadialGauge from '../common/KpiWithRadialGauge';
import KpiWithPie from '../common/KpiWithPie';
import PieChart from '../common/PieChart';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import RowTypography from 'components/Table/RowTypography';
import { fetchTableauTotalCost, fetchTableauCostByRole, getPepwiseSectorList, fetchTableauLicenseUtilization, fetchTableauCostBySector,fetchCostTableData,fetchTableauCostExport } from '../../../service/service'; // Add fetchTableauCostByRole import

// Utility function outside components to avoid duplication
const formatCost = (cost: number): string => {
	if (cost >= 1000000) {
		const millions = cost / 1000000;
		return `$${millions.toFixed(2)} MM`;
	}
	return `$${cost.toLocaleString()}`;
};

type FormatterProps = {
	row: any;
};

type TotalCostFormatterProps = {
	row: any;
};

const SectorFormatter: React.FC<FormatterProps> = ({ row }) => (
	<RowTypography>{row.sector || ''}</RowTypography>
);

const RoleFormatter: React.FC<FormatterProps> = ({ row }) => (
	<RowTypography>{row.role || ''}</RowTypography>
);

const LicenseCountFormatter: React.FC<FormatterProps> = ({ row }) => (
	<RowTypography>{row.licenseCount || ''}</RowTypography>
);


const TotalCostFormatter: React.FC<TotalCostFormatterProps> = ({ row }) => {
	return <RowTypography>{formatCost(row.totalCost || 0)}</RowTypography>;
};

type TableauCostProps = {
	filters?: {
		sector?: string | string[];
		filterOption?: string;
		startDate?: string;
		endDate?: string;
		filterList?: null | { [key: string]: any };
		chartTimePeriod?: string;
	};
	checkedListRef?: React.MutableRefObject<any>;
	timeRange?: string;
	selectedSectors?: string[];
	resetFiltersRef?: React.MutableRefObject<(() => void) | null>;
};

const TableauCost = ({ timeRange, selectedSectors, resetFiltersRef, filters: externalFilters, checkedListRef }: TableauCostProps) => {
	const [perLicenseRoleFilter, setPerLicenseRoleFilter] = useState('Creator');
	const [costBySectorsRoleFilter, setCostBySectorsRoleFilter] = useState('All');
	const [activeByLicenseType, setActiveByLicenseType] = useState<Record<string, number>>({});
	const [totalCostFilter, setTotalCostFilter] = useState('All');
	const [allSectors, setAllSectors] = useState<string[]>([]);
	const [areSectorsLoaded, setAreSectorsLoaded] = useState(false);
	const [costBySectorsData, setCostBySectorsData] = useState<Array<{ name: string; value: number }>>([]);
	const [isCostBySectorsLoading, setIsCostBySectorsLoading] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [filterDataOptions, setFilterDataOptions] = useState<any>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalElements: 0,
    pageSize: 50
  });

  const [filters, setFilters] = useState({
    sector: 'all',
    searchTerm: '',
    sortBy: 'sector',
    orderBy: 'ASC'
  });

  const [currentFilters, setCurrentFilters] = useState<Record<string, any>>({});

   const fetchFilterOptions = async () => {
      try {
        let sectors = await getPepwiseSectorList();
        const filterSectors = sectors?.data?.sectors.map((s: any) => s.Sector_Name) || [];
  
        setFilterDataOptions({
          sector: filterSectors,
          role: ['Creator', 'Viewer','Explorer']
        });
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };

     useEffect(() => {
        fetchFilterOptions();
      }, []);

	useEffect(() => {
	const loadSectors = async () => {
		try {
		const response = await getPepwiseSectorList();
		const sectorNames =
			response?.data?.sectors
			?.map((sector: any) => sector?.Sector_Name)
			?.filter((name: string) => !!name && name !== 'Unknown') || [];
		setAllSectors(sectorNames);
		console.log("areSectorsLoaded", areSectorsLoaded);
		console.log("getSectorPayload", getSectorPayload());
		} catch (error) {
		console.error('Error fetching sector list for Tableau reports:', error);
		} finally {
		setAreSectorsLoaded(true);
		}
	};

	loadSectors();
	}, []);

	const getSectorPayload = useCallback(() => {
		let sectors: string;
		
		if (checkedListRef?.current && checkedListRef.current.length > 0) {
			sectors = checkedListRef.current.join(',');
		} else if (typeof externalFilters?.sector === 'string' && externalFilters.sector.toLowerCase() !== 'all') {
			sectors = externalFilters.sector;
		} else {
			const sector = externalFilters?.sector;
			if (Array.isArray(sector) && sector.length > 0) {
				sectors = sector.join(',');
			} else {
				sectors = allSectors.join(',');
			}
		}
		
		// Always append OTHER if it's not already included
		const sectorList = sectors.split(',').map(s => s.trim());
		if (!sectorList.some(s => s.toUpperCase() === 'OTHER')) {
			sectors = `${sectors},OTHER`;
		}
		
		return sectors;
	}, [checkedListRef, externalFilters?.sector, allSectors]);
	
	// Add state for total cost data
	const [totalCostData, setTotalCostData] = useState({
		number: 0,
		change: 0,
		changeType: 'increase' as const,
		breakdown: {} as Record<string, any>
	});
	const [isTotalCostLoading, setIsTotalCostLoading] = useState(false);

	// Add state for Cost By Role data
	const [costByRoleData, setCostByRoleData] = useState<Array<{ name: string; value: number }>>([]);
	const [isCostByRoleLoading, setIsCostByRoleLoading] = useState(false);
	const [perLicenseCostByRoleData, setPerLicenseCostByRoleData] = useState<Array<{ name: string; value: number }>>([]);
	const [totalPurchasedCost, setTotalPurchasedCost] = useState<{ purchasedCost: number }>({ purchasedCost: 0 });

	useEffect(() => {
		if (resetFiltersRef) {
			resetFiltersRef.current = () => {
				setPerLicenseRoleFilter('Creator');
				setCostBySectorsRoleFilter('All');
				setTotalCostFilter('All');
         setFilters({
          sector: 'all',
          searchTerm: '',
          sortBy: 'sector',
          orderBy: 'ASC'
        });
        setCurrentFilters({});
			};
		}
		
		return () => {
			if (resetFiltersRef) {
				resetFiltersRef.current = null;
			}
		};
	}, [resetFiltersRef]);

	useEffect(() => {
		console.log('Cost tab - timeRange:', timeRange);
		console.log('Cost tab - selectedSectors:', selectedSectors);
	}, [timeRange, selectedSectors]);

	const perLicenseCost = useMemo(() => {

		const perLicenseCostNumber = perLicenseCostByRoleData.find(
   						 item => item.name === perLicenseRoleFilter)?.value ?? 0;
		return Number(perLicenseCostNumber.toFixed(2)); // round to 2 decimals

	  }, [perLicenseCostByRoleData, perLicenseRoleFilter]);
	
	  const utilizationCost = useMemo(() => {
		const utilizationCostNumber = perLicenseCostByRoleData.reduce(
			(sum, { name, value }) => {
				const activeCount = activeByLicenseType[name] ?? 0;

				return sum + activeCount *  value
			}, 0);
		return Number(utilizationCostNumber.toFixed(2)); // round to 2 decimals
	  }, [perLicenseCostByRoleData, activeByLicenseType]);

	  const utilizationPercentage = useMemo(() => {
		const utilizationCostNumber = perLicenseCostByRoleData.reduce(
			(sum, { name, value }) => {
				const activeCount = activeByLicenseType[name] ?? 0;

				return sum + activeCount *  value
			}, 0);
		const purchased = Number(totalPurchasedCost?.purchasedCost);
		const utilized = Number(utilizationCostNumber);

		if (!purchased) {
			return 0; 
		}

		return Math.round((utilized / purchased) * 100);
	  }, [perLicenseCostByRoleData, activeByLicenseType, totalPurchasedCost]);


	type FormattedCost = {  value: number; unit: string;};

	const formatCostValue = (num = 0): FormattedCost => {
		if (num >= 1000000) {
			return {value: Number((num / 1000000).toFixed(2)), unit: "MM"};
		} else if (num >= 1000){
			return {value: Number((num / 1000).toFixed(2)), unit: "K"};
		}
		return {value: num === 0 ? num : Number(num.toFixed(2)),
				unit: ''};
	};

    const fetchTableData = useCallback(async (page = 1, pageSize = 50) => {
    if (!areSectorsLoaded) return;

    setIsTableLoading(true);
    try {
      let sectorPayload = getSectorPayload();
      
      const hasSpecificSelection = checkedListRef?.current && checkedListRef.current.length > 0;
      const hasExternalSectorFilter = 
        (typeof externalFilters?.sector === 'string' && externalFilters?.sector?.toLowerCase() !== 'all') ||
        (Array.isArray(externalFilters?.sector) &&externalFilters?.sector && externalFilters?.sector?.length > 0);

      if (!hasSpecificSelection && !hasExternalSectorFilter) {
        sectorPayload = `${sectorPayload}`;
      }

      const params = {
        page,
        pageSize,
        sector: sectorPayload,
        searchTerm: filters.searchTerm,
        sortBy: filters.sortBy || "sector",
        orderBy: filters.orderBy || "ASC",
        filters: currentFilters,
        filterOption: timeRange
      };

      console.log('Fetching cost table data with params:', params);
      
      const response = await fetchCostTableData(params);
      
      if (response?.success && response?.data) {
        setTableData(response.data.costDistribution || []);
        setPagination(response.data.pagination || {
          currentPage: page,
          totalPages: 0,
          totalElements: 0,
          pageSize: pageSize
        });
      } else {
        setTableData([]);
        setPagination({
          currentPage: 1,
          totalPages: 0,
          totalElements: 0,
          pageSize: pageSize
        });
      }
    } catch (error) {
      console.error('Error fetching cost table data:', error);
      setTableData([]);
      setPagination({
        currentPage: 1,
        totalPages: 0,
        totalElements: 0,
        pageSize: pagination.pageSize
      });
    } finally {
      setIsTableLoading(false);
    }
  }, [
    areSectorsLoaded, 
    getSectorPayload, 
    filters, 
    currentFilters, 
    timeRange, 
    externalFilters, 
    checkedListRef,
    pagination.pageSize
  ]);

	const fetchTotalCost = useCallback(async () => {
		setIsTotalCostLoading(true);
		try {
			const payload = {
				role: totalCostFilter
			};
			
			const response = await fetchTableauTotalCost(payload);
			
			if (response?.success && response?.data) {
				const data = response.data;
				setTotalCostData({
					number: data.totalCost || 0,
					change: 0, 
					changeType: 'increase',
					breakdown: data.breakdown || {}
				});
			}
		} catch (error) {
			console.error('Error fetching total cost data:', error);
			setTotalCostData({
				number: 0,
				change: 0,
				changeType: 'increase',
				breakdown: {}
			});
		} finally {
			setIsTotalCostLoading(false);
		}
	}, [totalCostFilter]);

	// Fetch Cost By Role API
	const fetchCostByRole = useCallback(async () => {
		setIsCostByRoleLoading(true);
		try {
			const response = await fetchTableauCostByRole({});
			
			if (response?.success && response?.data) {
				// Transform the backend data to match the PieChart format
				const transformedData = response.data.map((item: any) => ({
					name: item.role,
					value: item.totalCost
				}));
				const perLicenseCost = response.data.map((item: any) => ({
					name: item.role,
					value: item.costPerLicense
				}));
				const totalPurchasedCost = response.data.reduce(
 					 (sum: number, item: any) => sum + (item.totalCost ?? 0), 0);
				setTotalPurchasedCost({ purchasedCost: totalPurchasedCost });		
				setCostByRoleData(transformedData);
				setPerLicenseCostByRoleData(perLicenseCost);

			}
		} catch (error) {
			console.error('Error fetching cost by role data:', error);
			// Set fallback data on error
			setCostByRoleData([]);
			setPerLicenseCostByRoleData([])
			setTotalPurchasedCost({ purchasedCost: 0 });
		} finally {
			setIsCostByRoleLoading(false);
		}
	}, []);
	
	const fetchCostBySector = useCallback(async () => {
		setIsCostBySectorsLoading(true);
		try {
			const payload = {
				role: costBySectorsRoleFilter,
				sector: getSectorPayload()
			};
			const response = await fetchTableauCostBySector(payload);
			
			if (response?.success && response?.data?.costBySector) {
				// Transform the backend data to match the PieChart format
				const transformedData = response.data.costBySector.map((item: any) => ({
					name: item.sector,
					value: item.totalCost
				}));
				setCostBySectorsData(transformedData);
			}
		} catch (error) {
			console.error('Error fetching cost by sector data:', error);
			// Set fallback data on error
			setCostBySectorsData([]);
		} finally {
			setIsCostBySectorsLoading(false);
		}
	}, [costBySectorsRoleFilter, getSectorPayload]);

 const handleFilterChange = (filter: { [key: string]: any }) => {
    const filterListMap: { [key: string]: any } = {};
    Object.keys(filter).forEach((key) => {
      const value = filter[key];
      if (value != null && (!Array.isArray(value) || value.length > 0)) {
        filterListMap[key] = value;
      }
    });

    setCurrentFilters(filterListMap);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSorting = useCallback((sorting: any) => {
    setFilters(prev => ({
      ...prev,
      sortBy: sorting.sortBy,
      orderBy: sorting.sortDir
    }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, []);

  const handlePageChange = useCallback((event: any, value: any) => {
    setPagination(prev => ({ ...prev, currentPage: value }));
    fetchTableData(value, pagination.pageSize);
  }, [fetchTableData, pagination.pageSize]);

  const handlePageSizeChange = useCallback((event: any) => {
    const newSize = Number.parseInt(event.target.value);
    setPagination(prev => ({ ...prev, pageSize: newSize, currentPage: 1 }));
    fetchTableData(1, newSize);
  }, [fetchTableData]);

  const handleExport = useCallback(async () => {
    try {
      const sectorPayload = getSectorPayload();
      const hasSpecificSelection = checkedListRef?.current && checkedListRef.current.length > 0;
      const hasExternalSectorFilter = 
        (typeof externalFilters?.sector === 'string' && externalFilters?.sector?.toLowerCase() !== 'all') ||
        (Array.isArray(externalFilters?.sector) && externalFilters?.sector && externalFilters?.sector?.length > 0);

      let finalSectorPayload = sectorPayload;
      if (!hasSpecificSelection && !hasExternalSectorFilter) {
        finalSectorPayload = `${sectorPayload},OTHER`;
      }

      const cleanFilters = Object.fromEntries(
        Object.entries(currentFilters).filter(([_, v]) => 
          Array.isArray(v) ? v.length > 0 : v != null
        )
      );

      const requestBody = {
        ...filters,
        sector: finalSectorPayload,
        filterBy: cleanFilters,
        filterOption: timeRange,
        exportColumns: defaultCostColumns,
        formatType: "EXCEL",
        tableName: "tableau-cost-monitoring",
      };

      console.log('Exporting cost data with params:', requestBody);
      
      
       const res = await fetchTableauCostExport(requestBody);
       console.log(res)

    } catch (error) {
      console.error('Error exporting Tableau Cost data:', error);
    }
  }, [filters, currentFilters, getSectorPayload, timeRange, externalFilters, checkedListRef]);

  const handleSearch = async (searchDetails: any, columnKey: any) => {
    if (searchDetails?.length >= 3) {
      setFilters(prev => ({
        ...prev,
        searchTerm: searchDetails
      }));
    } else if (searchDetails?.length === 0) {
      setFilters(prev => ({
        ...prev,
        searchTerm: ''
      }));
    }
  };

	const fetchActiveLicenseCost = useCallback(async () => {
		try {
			
			const payload = {
        	sector: "LATAM,CGF,EUROPE,PBNA,PFNA,AMESA/APAC,OTHER",
        	filterOption: timeRange || externalFilters?.filterOption || 'LAST_30_DAYS'
      };
	const response = await fetchTableauLicenseUtilization(payload);
			
	if (response?.success && response?.data) {
		const data = response.data;
		// const totalByLicenseType = data.totalsByLicenseType
		const activeByLicenseType = data.activeByLicenseType;
		setActiveByLicenseType (activeByLicenseType)
			}
		} catch (error) {
			console.error('Error fetching active License cost data:', error);
			setActiveByLicenseType({});
		} finally {
		}
	}, [externalFilters?.filterOption]);


	// Fetch total cost when filter changes
	useEffect(() => {
		fetchTotalCost();
		fetchActiveLicenseCost();
	}, [fetchTotalCost]);

	// Fetch cost by role on component mount
	useEffect(() => {
		fetchCostByRole();
	}, [fetchCostByRole]);
	
	useEffect(() => {
		if (areSectorsLoaded) {
			fetchCostBySector();
      fetchTableData(1, pagination.pageSize);
		}
	}, [fetchCostBySector, areSectorsLoaded,fetchTableData, pagination.pageSize]);


	const costByRoleColorMapping = {
		'Creator': '#4A90E2',
		'Explorer': '#FFA500',
		'Viewer': '#D82B51'
	};

	const costBySectorsColorMapping = {
		'PBNA': '#4A90E2',
		'PFNA': '#01C008',
		'LATAM': '#FFA500',
		'AMESA/APAC': '#9B59B6',
		'Europe': '#59acb1ff',
		'CGF': '#D82B51',
		'Other': '#ac189fff',
		'Unassigned': '#b4b4b4ff'
	};

  const costMonitoringColumns = [
    {
      key: 'sector',
      name: 'Sector',
      minWidth: 200,
      formatter: SectorFormatter,
      resizable: true,
      flexGrow: 1
    },
    {
      key: 'role',
      name: 'Role',
      minWidth: 150,
      formatter: RoleFormatter,
      resizable: true,
      flexGrow: 1
    },
    {
      key: 'licenseCount',
      name: 'License Count',
      minWidth: 150,
      formatter: LicenseCountFormatter,
      resizable: true,
      flexGrow: 1
    },
    {
      key: 'totalCost',
      name: 'Total Cost',
      minWidth: 200,
      formatter: TotalCostFormatter,
      resizable: true,
      flexGrow: 1
    }
  ];

  const defaultCostColumns = [
    'Sector',
    'Role',
    'License Count',
    'Total Cost'
  ];

	return (
		<Box sx={{ width: '100%' }}>
			{/* First Row - Total Cost, Cost By Sectors */}
			<Grid container spacing={2} sx={{ mb: 2 }}>
				{/* Total Cost with Pie Chart */}
				{/* Cost By Sectors */}
				<Grid item xs={12} md={6}>
					<KpiWithPie
						isLoading={isTotalCostLoading || isCostBySectorsLoading}
						title="Total Cost"
						hasTooltip={true}
						tooltipText='Total cost of purchased licenses and total cost of used licenses by sector'
						number={`$ ${formatCostValue(totalCostData?.number ?? 0).value}`}
						numberSuffix={formatCostValue(totalCostData?.number ?? 0).unit}
						pieData={costBySectorsData}
						colorMapping={costBySectorsColorMapping}
						hasDropdown={true}
						dropdownValue={totalCostFilter}
						dropdownOptions={[
							{ label: 'All', value: 'All' },
							{ label: 'Creator', value: 'Creators' },
							{ label: 'Viewer', value: 'Viewers' },
							{ label: 'Explorer', value: 'Explorers' }
						]}
						onDropdownChange={(value) => {
							setTotalCostFilter(value);
							setCostBySectorsRoleFilter(value);
						}}
						containerProps={{
							sx: {
								minHeight: '280px',
							}
						}}
					/>
				</Grid>
			
				{/* Cost By Role - Now using backend data */}
				<Grid item xs={12} md={6}>
					<PieChart
						isLoading={isCostByRoleLoading}
						title="Cost By Role"
						hasTooltip={true}
						tooltipText='Total cost of purchased licenses by Creator, Explorer, and Viewer'
						isTextTooltip={true}
						data={costByRoleData}
						colorMapping={costByRoleColorMapping}
						showDollarAmounts={true}
						containerProps={{
							sx: {
								minHeight: '280px',
							}
						}}
					/>
				</Grid>
			</Grid>

			{/* Second Row - Cost By Role, Per License Cost, Total Purchased Cost */}
			<Grid container spacing={2} sx={{ mb: 2 }}>

				{/* Per License Cost */}
				<Grid item xs={12} md={6}>
					<KpiCard
						isLoading={false}
						title="Per License Cost"
						hasTooltip={true}
						tooltipLabel='Cost of Creator, Explorer, and Viewer licenses'
						theme="light"
						isTextTooltip={true}
						isCost = {true}
						suffixFontSize={24}
						number={`${perLicenseCost}`}
						units={''}
						hasDropdown={true}
						dropdownValue={perLicenseRoleFilter}
						dropdownOptions={[
							{ label: 'Creator', value: 'Creator' },
							{ label: 'Viewer', value: 'Viewer' },
							{ label: 'Explorer', value: 'Explorer' }
						]}
						onDropdownChange={(value) => setPerLicenseRoleFilter(value)}
						containerProps={{
							sx: {
								minHeight: '280px',
							}
						}}
					/>
				</Grid>

				{/* Total Purchased Cost with Radial Gauge */}
				<Grid item xs={12} md={6}>
					<KpiWithRadialGauge
						isLoading={false}
						title="Total Purchased Cost"
						hasTooltip={true}
						tooltipText='Total cost of purchased and used licenses'
						number={formatCostValue (totalPurchasedCost?.purchasedCost ?? 0).value}
						numberSuffix={formatCostValue (totalPurchasedCost?.purchasedCost ?? '').unit}
						gaugeValue={utilizationPercentage}
		
						utilizedCost={"$ " + formatCostValue(utilizationCost).value +
										 " " + formatCostValue(utilizationCost).unit}
						gaugeColor="#fa7837"
						backgroundColor="#001e38"
						containerProps={{
							sx: {
								minHeight: '280px',
							}
						}}
					/>
				</Grid>
			</Grid>

			<Box mt={2}>
				<CustomizedDataGrid
				    title="Cost Monitoring"
          tableColumns={costMonitoringColumns}
          tableData={tableData}
          tableType="tableau-cost-monitoring"
          defaultColumnNames={defaultCostColumns}
          onSorting={handleSorting}
          currSorting={{
            sortBy: filters.sortBy,
            sortDir: filters.orderBy
          }}
          filterDataOptions={filterDataOptions}
          searchDataValues={{}}
          onFilterCheck={handleFilterChange}
          currFilter={currentFilters}
          onFilterSearch={handleSearch}
          onHeaderSelectAll={() => {}}
          onFilterClick={() => {}}
          showExport={true}
          fileName="tableau-cost-report"
          tableName="tableau-cost-monitoring"
          formatType="EXCEL"
          exportColumns={defaultCostColumns}
          exportTableData={handleExport}
          page={pagination.currentPage}
          pageSize={pagination.pageSize}
          paginationData={pagination}
          listLength={tableData.length}
          handlepageSizeChange={handlePageSizeChange}
          handlePageChange={handlePageChange}
          showGoToPage={false}
          isFetching={isTableLoading}
          isDateRangeFilter={false}
          isDisabled={false}
          pageSizeOptions={[25, 50, 100, 200]}
				/>
			</Box>
		</Box>
	);
};

export default TableauCost;